/**
 * Появление при скролле и счётчик ставки.
 *
 * Оба хука безопасны в средах без `IntersectionObserver` (jsdom в тестах) и
 * при включённом `prefers-reduced-motion`: блок сразу считается видимым,
 * счётчик сразу показывает конечное значение.
 */
import { useCallback, useEffect, useState } from 'react';
import { prefersReducedMotion } from './motion';

const VISIBLE_CLASS = 'is-visible';

function markVisible(node: Element): void {
  node.classList.add(VISIBLE_CLASS);
}

/**
 * Возвращает ref-колбэк: как только элемент попадает в область просмотра,
 * на него ставится класс `is-visible` и наблюдение снимается — эффект
 * одноразовый, при обратном скролле блок не мигает.
 */
export function useRevealRef<T extends Element>(): (node: T | null) => void {
  const [node, setNode] = useState<T | null>(null);
  useEffect(() => {
    if (!node) return;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      markVisible(node);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          markVisible(node);
          observer.disconnect();
        }
      },
      { threshold: 0.05 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node]);
  return useCallback((element: T | null) => setNode(element), []);
}

export function useSeen<T extends Element>(): [(node: T | null) => void, boolean] {
  const [node, setNode] = useState<T | null>(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!node || seen) return;
    if (typeof IntersectionObserver === 'undefined' || prefersReducedMotion()) {
      setSeen(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [node, seen]);
  return [useCallback((element: T | null) => setNode(element), []), seen];
}

const COUNT_UP_DURATION_MS = 700;

/**
 * Набегающий счётчик. Применяется только к ставке за м² — величине
 * справочной. Итоговая вилка клиента таким образом не анимируется никогда:
 * цена не должна «крутиться» перед тем, кто её читает.
 */
export function useCountUp(value: number, active: boolean): number {
  const [shown, setShown] = useState(() => (prefersReducedMotion() ? value : 0));

  useEffect(() => {
    if (!active || prefersReducedMotion() || typeof requestAnimationFrame !== 'function') {
      setShown(value);
      return;
    }

    let frame = 0;
    const started = performance.now();
    const step = (now: number): void => {
      const progress = Math.min(1, (now - started) / COUNT_UP_DURATION_MS);
      // Замедление к концу: последние цифры не мелькают.
      const eased = 1 - (1 - progress) ** 3;
      setShown(Math.round(value * eased));
      if (progress < 1) frame = requestAnimationFrame(step);
    };
    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [value, active]);

  return shown;
}
